import type { Passage } from "./types";

export const passage: Passage = {
  slug: "2-timothy-1-7",
  reference: "2 Timothy 1:7",
  title: "2 Timothy 1:7: not a spirit of fear, read in context",
  h1: "2 Timothy 1:7, and what Timothy was actually afraid of",
  description:
    "God has not given us a spirit of fear is used against anxiety. The verse is about a specific reluctance, and the sentence after it names what it was.",
  targets: [
    "2 timothy 1 7 meaning",
    "god has not given us a spirit of fear explained",
    "power love and sound mind meaning",
  ],
  published: "2026-09-03",
  standfirst:
    "It is quoted at frightened people as though fear itself were the problem. Paul is writing from prison to somebody who has gone quiet, and the fear has a subject.",
  verse: {
    text: "For God didn't give us a spirit of fear, but of power, love, and self-control.",
    ref: "2 Timothy 1:7",
  },
  context: [
    {
      text: "For this cause, I remind you that you should stir up the gift of God which is in you through the laying on of my hands.",
      ref: "2 Timothy 1:6",
    },
    {
      text: "Therefore don't be ashamed of the testimony of our Lord, nor of me his prisoner; but endure hardship for the Good News according to the power of God,",
      ref: "2 Timothy 1:8",
    },
  ],
  takeaways: [
    "Verse 8 names the fear: being ashamed — of the message, and of Paul, who is in prison. It is social fear about association, not anxiety in general.",
    "The word translated fear is not the ordinary New Testament word for fear; it is closer to timidity or cowardice, and it appears only here.",
    "Read as a verdict on anxiety it becomes an accusation. Read as written it is an encouragement to a specific person who has gone quiet under pressure.",
    "The third item, usually rendered sound mind or self-control, is about disciplined judgement — not about mental health.",
  ],
  body: [
    {
      type: "h2",
      id: "the-situation",
      text: "The situation the letter is written into",
    },
    {
      type: "p",
      text: "2 Timothy reads as a last letter. Paul is in custody, most of his companions have gone, and he asks Timothy to come before winter and to bring the cloak and the scrolls he left behind. The letter's recurring anxiety is not about Timothy's feelings but about whether he will keep going.",
    },
    {
      type: "p",
      text: "Verse 6 asks him to stir up the gift that is in him — the language implies something that has gone quiet. Verse 7 gives the reason. Verse 8 says what the reluctance is about: do not be ashamed of the testimony, or of me his prisoner.",
    },
    {
      type: "p",
      text: "The letter's other details point the same way. Paul names people who have deserted him and one household that was not ashamed of his chain, singling them out for it — which only makes sense as praise if the shame was the live problem in the room. He then asks Timothy to come, which is itself a request to be publicly associated.",
    },
    {
      type: "h2",
      id: "the-word",
      text: "The word is not fear",
    },
    {
      type: "p",
      text: "The Greek is deilia, which occurs nowhere else in the New Testament and carries the sense of timidity or cowardice — the failure of nerve rather than the emotion of being afraid. The ordinary word for fear, phobos, is used elsewhere by Paul, including approvingly.",
    },
    {
      type: "p",
      text: "That distinction matters because it decides who the verse is for. Deilia is a shrinking back from doing something; anxiety is a condition. The verse addresses the first.",
    },
    {
      type: "p",
      text: "It is also worth knowing what association with Paul cost at this point. He was in Roman custody on a capital charge, and the letter says plainly that everyone in Asia had turned away from him. Being publicly connected to that man was not an abstract social risk; it was the sort of thing that could reasonably end a person's usefulness, and possibly more.",
    },
    {
      type: "h2",
      id: "the-three-things",
      text: "The three things given instead",
    },
    {
      type: "p",
      text: "Power, love, and a third word that translations render as self-control, sound mind or self-discipline. It means something like a well-ordered mind — judgement that is not run by impulse.",
    },
    {
      type: "p",
      text: "None of the three is a feeling. They are capacities, given rather than achieved, and they are listed as the alternative to shrinking back from a hard, public, costly thing.",
    },
  ],
  misreading: {
    claim:
      "That it teaches that fear is not from God — and therefore that anxiety, panic and dread are spiritual failures or attacks to be rebuked rather than experiences to be treated.",
    correction: [
      "This use of the verse is common and it does real damage. Told that their fear did not come from God, an anxious person hears that the thing happening in their body is an accusation, and the shame that follows is worse than the anxiety was.",
      "The verse is not about that. The word is deilia, timidity, used only here; the reluctance in view is named in the next sentence as being ashamed of the message and of an imprisoned friend. Paul is addressing a young colleague who has gone quiet under social pressure, and telling him what he has been given for the job.",
      "Which leaves the verse doing something useful and specific: it is for the moment before saying the difficult true thing, not for the moment at three in the morning when your heart will not slow down. That one has other verses, and it also has doctors.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Read verses 6 to 8 as one sentence, because that is nearly what they are. The gift that needs stirring, the three things given, and the shame that is the actual obstacle.",
    },
    {
      type: "p",
      text: "Use it before the conversation you are avoiding. For the other thing — the racing chest, the three in the morning — {a prayer that does not tell you to calm down} is the better page.",
      links: {
        "a prayer that does not tell you to calm down":
          "/prayers/prayer-for-anxiety",
      },
    },
    {
      type: "note",
      text: "It is also worth noticing what Paul does not say. He does not tell Timothy that the pressure is imaginary, that the shame is irrational, or that a stronger Christian would not feel it. He names the reluctance, states what has been given instead, and then in the next breath asks him to endure hardship — which concedes that the hard thing is genuinely hard.",
    },
    {
      type: "note",
      text: "If somebody has quoted this at you about your anxiety, they were reaching for the nearest verse. It was not written about you.",
    },
  ],
  faq: [
    {
      q: "What does 2 Timothy 1:7 mean?",
      a: "That God's gift to Timothy was not timidity but power, love and disciplined judgement — said to a young colleague who had gone quiet under pressure, with the next verse naming the fear as shame about the message and about Paul's imprisonment.",
    },
    {
      q: "Does 2 Timothy 1:7 mean anxiety is sinful?",
      a: "No. The word is deilia, timidity or cowardice, which appears nowhere else in the New Testament and refers to shrinking back from an action. Anxiety as a condition is not what the sentence is about.",
    },
    {
      q: "What does sound mind mean in this verse?",
      a: "The Greek suggests a well-ordered mind — judgement that is not driven by impulse. It is a capacity rather than a state of mental health, and it sits alongside power and love as things given for a task.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so the verse is printed whole rather than referred to. It ends on 'self-control' where the King James has 'a sound mind' — worth knowing, since the last word is usually the reason the verse is quoted.",
    },
  ],
  related: ["phone-addiction-bible-verses", "christian-app-blocker-guide"],
  relatedTopics: [
    "bible-verses-for-anxiety-at-night",
    "bible-verses-about-strength",
  ],
  relatedPassages: ["isaiah-41-10", "philippians-4-6-7"],
};
